import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFolder, deleteDrawing, deleteFolder, getFolderTree, moveDrawingToFolder,
  patchFolder,
} from '../api/client';
import { invalidateDrawingGraph } from '../lib/graphRefresh';
import type { DrawingScope, DrawingSummary, FolderNode, SipEntry } from '../types';

/**
 * 图纸区多级文件夹树（v0.32 需求 1）。
 *
 * 需求原话：「图纸区域需要能右键新建文件夹，文件夹里面还能建文件夹。方便图纸分类管理」
 *
 * 交互设计（三条硬约定）
 *  1. **右键是唯一的建/改入口**：在空白处右键 = 新建文件夹；在文件夹上右键 =
 *     新建子文件夹 / 重命名 / 删除 / 移动到…；在图纸上右键 = 移动到… / 删除图纸。
 *     不做「悬浮出现 + 号」那类隐藏入口 —— 图纸区就一小条，常驻按钮会挤掉文件名。
 *  2. **删除文件夹绝不动图纸**：后端会把里面的图纸退回根目录，前端提示必须
 *     **如实报数**（「已删除 2 个文件夹，3 张图纸已退回根目录」），
 *     而不是只说「已删除」——否则用户会以为图纸没了。
 *  3. **折叠态本地持久化**（`gdt_balloon_folders_open`）：几十个夹子逐个展开很烦，
 *     刷新后应当保持原样。用 Set<number> 存 JSON 数组。
 *
 * v0.56（用户 2026-10-16）：「**零件主数据归类到文件夹了，图纸跟作业指导书那里
 * 没统一变动，三者需要联动**」⇒ 每个夹子改**双计数**「N 张图纸 · M 个零件」，
 * 零件那一格独立可点、跳到零件主数据的同一个文件夹（见 `TreeNode` 与 Props）。
 * ★ 口径：**只统一展示、不动数据** —— 图纸与零件各归各的。
 */

const LS_OPEN = 'gdt_balloon_folders_open';

function loadOpen(): Set<number> {
  try {
    const raw = localStorage.getItem(LS_OPEN);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set<number>(Array.isArray(arr) ? arr.map(Number) : []);
  } catch {
    return new Set<number>();
  }
}

interface Ctx {
  x: number;
  y: number;
  kind: 'root' | 'folder' | 'drawing';
  folderId?: number;
  drawing?: DrawingSummary;
}

interface Props {
  selectedDrawingId: number | null;
  /** `id=null` = 清空选中（图纸被删时用）。 */
  onSelect: (id: number | null, d?: DrawingSummary) => void;
  onToast: (msg: string, type?: 'success' | 'error') => void;
  /**
   * v0.50：图纸行上的「SIP」按钮 —— 打开作业指导书的编辑页。
   *
   * ★ 为什么按钮放在**图纸行**而不是只在零件主数据里：SIP 的检验明细就是
   *   这张图的气泡号 + 尺寸 + 公差，在图纸边上点最顺手（用户 2026-10-14
   *   拍板「3、两个都要」= 零件行 + 图纸行都放入口）。
   * ⚠️ 这里**只**传 `{kind:'drawing', id}`；「这张图归户到哪个零件」由 SIP 页
   *   自己读（表头只读），本组件不参与表头取值。 */
  onOpenSip?: (e: SipEntry) => void;
  /**
   * v0.40（用户 2026-10-10 定的顶层结构 `BOM —— 图纸`）：列表的范围。
   * 缺省 = `all`（原文件夹树）。**只有这一份图纸列表实现**，范围只改它取哪一段。
   *
   * v0.42（2026-10-11）：`part` 从「只列本零件的图」改成「**全集 + 本零件置顶高亮**」。
   * 原因见下方 `scoped` 的注释（同一动作刷新前后两种观感）。
   */
  scope?: DrawingScope;
  /**
   * v0.47：能否改（新建文件夹 / 重命名 / 移动 / 删图纸）。
   * ★ 右键菜单是**本组件唯一的建改入口**，所以只读只需把 `openMenu` 关掉 +
   *   藏掉「＋ 新建文件夹」按钮，不必逐个菜单项去判 —— 少一处判断就少一个漏点。
   */
  canEdit?: boolean;
  /**
   * v0.56（用户 2026-10-16 原话）：「**零件主数据归类到文件夹了，图纸跟作业指导书
   * 那里没统一变动，三者需要联动**」。
   *
   * ★ 文件夹树本来就是**图纸与零件共用的一棵树**（`GET /api/folders`
   *   一次给出 `drawing_count` 与 `part_count`），只是图纸区一直只显示图纸那一半
   *   ⇒ 用户在零件主数据里把零件拖进夹子，回到图纸区**看不出任何变化**，
   *   观感就是「三个页面各说各的」。
   *
   * ★ 口径（经用户确认）：**不动数据，只统一展示** —— 图纸与零件各有自己的归属，
   *   各归各的，点零件数只是**跳过去看**，不会把图纸或零件互相搬运。
   *   所以这里只向外抛一个 `folderId`，由 App 切页并让零件页落到该文件夹。
   */
  onOpenPartsFolder?: (folderId: number) => void;
}

export function DrawingTree({ selectedDrawingId, onSelect, onToast, onOpenSip, scope,
                              canEdit = true, onOpenPartsFolder }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<Set<number>>(loadOpen);
  const [ctx, setCtx] = useState<Ctx | null>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  /** 菜单根 DOM。用于「点菜单自己不要关」的判定 —— 见下面的 mousedown 说明。 */
  const menuRef = useRef<HTMLDivElement>(null);

  const treeQ = useQuery({
    queryKey: ['folders'],
    queryFn: getFolderTree,
    refetchInterval: 10_000,     // 兜底：别处（识别完成）新增的图纸能自己冒出来
  });

  const refresh = useCallback(() => {
    // v0.56：改走**统一入口** —— 图纸区的增删改同样影响 SIP、零件抽屉的
    // 「关联图纸」、台账的图纸数。原先只刷 folders/drawings 两键，
    // 于是"删了图纸、切到 SIP 还看得见"（用户报的那条同步问题）。
    invalidateDrawingGraph(qc);
  }, [qc]);

  // ---------------------------------------------------------------- 折叠态
  const toggle = useCallback((id: number) => {
    setOpen((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      try { localStorage.setItem(LS_OPEN, JSON.stringify([...n])); } catch { /* 忽略 */ }
      return n;
    });
  }, []);

  // 新建出来的文件夹应当**自动展开**父级，否则用户会以为没建成
  const ensureOpen = useCallback((id: number) => {
    setOpen((prev) => {
      if (prev.has(id)) return prev;
      const n = new Set(prev); n.add(id);
      try { localStorage.setItem(LS_OPEN, JSON.stringify([...n])); } catch { /* 忽略 */ }
      return n;
    });
  }, []);

  // ---------------------------------------------------------------- 右键菜单
  // 「点别处就关」，但**点菜单自己绝不能关**。
  //
  // 🐞 v0.33 修：此前这里是 `const close = () => setCtx(null)`，并指望菜单容器上的
  //    `onMouseDown={e => e.stopPropagation()}` 把它挡住。**挡不住** ——
  //    window 上的监听注册在 **capture 阶段**（第 3 个参数 true），它在事件到达
  //    菜单容器（target/冒泡阶段）**之前**就已执行，stopPropagation 根本来不及跑。
  //    后果：用户按下左键的瞬间菜单就被卸载，`click` 永远落不到菜单项上 ——
  //    现象正是「右键出来的内容无法左键点击」。
  // 正解：在关闭回调里判断事件目标是否落在菜单 DOM 内（menuRef.contains）。
  //
  // ⚠️ 复现/回归必须用**真实鼠标事件序列**（CDP `Input.dispatchMouseEvent`
  //    的 mousePressed → mouseReleased）。用合成 `el.click()` 不会产生 mousedown，
  //    这个缺陷**测不出来** —— v0.32 的 64 PASS 走查就是这样漏掉它的。
  useEffect(() => {
    if (!ctx) return;
    // 同一支回调同时挂在 mousedown 与 scroll 上 ⇒ 形参只能取二者共同基类 Event
    // （写成 MouseEvent 会让 scroll 那个重载不匹配，TS 直接报 2769）。
    const close = (e: Event) => {
      const t = e.target as Node | null;
      if (menuRef.current && t && menuRef.current.contains(t)) return;
      setCtx(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCtx(null); };
    window.addEventListener('mousedown', close, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctx]);

  const openMenu = (e: React.MouseEvent, c: Omit<Ctx, 'x' | 'y'>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEdit) return;                    // v0.47：只读角色没有右键菜单
    // 贴近右/下边界时向内收，避免菜单被裁掉
    const W = 172, H = 200;
    setCtx({
      ...c,
      x: Math.min(e.clientX, window.innerWidth - W - 8),
      y: Math.min(e.clientY, window.innerHeight - H - 8),
    });
  };

  // ---------------------------------------------------------------- 数据操作
  const foldersFlat = useMemo(
    () => flatten(treeQ.data?.folders ?? []), [treeQ.data?.folders]);

  const createMut = useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId: number | null }) =>
      createFolder(name, parentId),
    onSuccess: (_n, v) => {
      if (v.parentId != null) ensureOpen(v.parentId);
      onToast(`已新建文件夹「${v.name}」`, 'success');
      refresh();
    },
    onError: (e: any) => onToast(`新建失败：${e.message}`, 'error'),
  });

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      patchFolder(id, { name }),
    onSuccess: (n) => { onToast(`已重命名为「${n.name}」`, 'success'); refresh(); },
    onError: (e: any) => onToast(`重命名失败：${e.message}`, 'error'),
  });

  const moveFolderMut = useMutation({
    mutationFn: ({ id, parentId }: { id: number; parentId: number | null }) =>
      patchFolder(id, { parent_id: parentId }),
    onSuccess: (_n, v) => {
      if (v.parentId != null) ensureOpen(v.parentId);
      onToast('已移动文件夹', 'success');
      refresh();
    },
    onError: (e: any) => onToast(`移动失败：${e.message}`, 'error'),
  });

  const delFolderMut = useMutation({
    mutationFn: (id: number) => deleteFolder(id),
    onSuccess: (r) => {
      const extra = r.drawings_to_root
        ? `，${r.drawings_to_root} 张图纸已退回根目录（图纸没有被删）`
        : '';
      // v0.56：文件夹也进回收站（含整棵子树与归属关系）⇒ 如实说明可恢复。
      onToast(`已删除 ${r.deleted_folders} 个文件夹${extra}（可在「回收站」恢复）`, 'success');
      refresh();
    },
    onError: (e: any) => onToast(`删除失败：${e.message}`, 'error'),
  });

  const moveDrawingMut = useMutation({
    mutationFn: ({ id, folderId }: { id: number; folderId: number | null }) =>
      moveDrawingToFolder(id, folderId),
    onSuccess: (r, v) => {
      const to = v.folderId == null ? '根目录'
        : (foldersFlat.find((f) => f.id === v.folderId)?.name ?? '文件夹');
      if (v.folderId != null) ensureOpen(v.folderId);
      // v0.57：图纸的文件夹归属**跟随它的归属零件** —— 已归户的图纸一移动，
      // 零件（连同它名下**全部**图纸）也跟着走，而零件那一侧是**另一个页签**。
      // ⇒ 只用后端回的 `moved_parts` / `moved_drawings` 说清楚动了什么，
      //   不许写死（未归户的图纸只动自己，两种情况文案必须不同）。
      const extra = r?.moved_parts
        ? `（归属零件与名下 ${r.moved_drawings ?? 0} 张图纸一并移动）`
        : '';
      onToast(`已移动到「${to}」${extra}`, 'success');
      refresh();
    },
    onError: (e: any) => onToast(`移动失败：${e.message}`, 'error'),
  });

  const delDrawingMut = useMutation({
    mutationFn: (id: number) => deleteDrawing(id),
    onSuccess: (_r, id) => {
      if (selectedDrawingId === id) onSelect(null);
      // v0.56：如实说清「还能捞回来」—— 不然用户删完就以为没了，
      // 转身去重新上传，库里的回收站条目反而永远留着。
      onToast('已删除图纸（可在「回收站」恢复）', 'success');
      refresh();
    },
    onError: (e: any) => onToast(`删除失败：${e.message}`, 'error'),
  });

  // ---------------------------------------------------------------- 渲染
  const tree = treeQ.data;
  const allDrawings = useMemo(() => tree?.drawings ?? [], [tree?.drawings]);
  const rootDrawings = useMemo(
    () => allDrawings.filter((d) => (d as any).folder_id == null),
    [allDrawings]);
  const total = allDrawings.length;

  // v0.40：范围过滤。`all` = 原样（文件夹树）；`part` / `unassigned` = 平铺只列图纸。
  // ★ 不新写第二个图纸列表组件 —— 本组件是「图纸列表」的**唯一实现**，
  //   范围只改它取哪一段、以及是否渲染文件夹层（见铁律「同一语义几份实现」）。
  //
  // v0.42（2026-10-11 用户口径：「全部图纸 + 本零件置顶高亮」）：
  //   `part` 范围**不再只列本零件的图**，而是「全集 + 本零件置顶」。
  //   为什么改：原实现只列本零件的图，于是点「1 张 ▸」进来只看到 1 张；
  //   而 `scope` **不入 localStorage**，刷新页面后范围被重置成 `all` ⇒ 又变成
  //   全部。同一个动作两种观感，用户的原话正是「进去是单独一张、刷新后全都出现了」。
  //   改成「全集 + 置顶」后，**刷新前后集合完全相同**，只剩置顶与高亮的差别。
  const scopeKind = scope?.kind ?? 'all';
  const scoped = useMemo(() => {
    // ⚠️ 判据必须直接写在 `scope` 上：`scopeKind === 'part'` 是**另一个变量**，
    //    TS 不会据此收窄 `scope` 的联合类型（会报 `Property 'id' does not exist
    //    on type 'DrawingScope'`）。这类"看着像 any 的问题"其实是收窄失效。
    if (scope?.kind === 'part') {
      return allDrawings;
    }
    if (scope?.kind === 'unassigned') {
      return allDrawings.filter((d) => d.part_master_id == null);
    }
    return allDrawings;
  }, [allDrawings, scope]);

  /** `part` 范围下要**置顶**的那一段（不属于它的在下面单列一段「其他图纸」）。
   *
   *  ⚠️ 两段都从 `allDrawings` 里筛，**顺序互不干扰**（各自保持后端给出的原序），
   *     不就地 sort —— sort 会让「其他图纸」的相对顺序每次都可能变。 */
  const minePartId = scope?.kind === 'part' ? scope.id : null;
  const mine = useMemo(
    () => (minePartId == null ? [] : allDrawings.filter((d) => d.part_master_id === minePartId)),
    [allDrawings, minePartId]);
  const rest = useMemo(
    () => (minePartId == null ? [] : allDrawings.filter((d) => d.part_master_id !== minePartId)),
    [allDrawings, minePartId]);

  /** 头部括号里的数 = **实际列出来的条数**（`part` 范围下已是全集）。
   *
   *  ⚠️ 数据没到时报 `…` 而**不是 `0`** —— 图纸数据是进图纸区才第一次请求的，
   *     把「还没加载」显示成「0 张」，用户会当场当成结论（本项目铁律：
   *     「永远是 0 的诊断」与「已经死掉的诊断」在界面上长得一模一样）。 */
  const loadingScope = scopeKind !== 'all' && treeQ.isLoading;
  const shown = loadingScope ? '…' : (scopeKind === 'all' ? total : scoped.length);

  const askName = (
    title: string, initial: string, onOk: (v: string) => void,
  ) => setDialog({
    kind: 'input', title, initial,
    hint: '同一级下不能重名；名称不能包含 / 或 \\',
    onOk,
  });

  const askMove = (
    title: string, currentId: number | null, onOk: (v: number | null) => void,
  ) => setDialog({
    kind: 'move', title,
    current: currentId,
    folders: foldersFlat.map((f) => ({ id: f.id, label: f.path })),
    onOk,
  });

  return (
    <aside className="sidebar">
      <div className="panel">
        <div className="panel-head">
          <span>
            {scopeKind === 'unassigned' ? '未归户图纸' : '图纸列表'}
            {' '}<em>({shown})</em>
            {scopeKind === 'part' && !loadingScope && (
              <span className="tree-scopenote">本零件 {mine.length} 张已置顶</span>
            )}
          </span>
          {scopeKind === 'all' && canEdit && (
            <button
              className="tree-newbtn"
              title="新建文件夹（也可以在下方空白处右键）"
              onClick={() => askName('新建文件夹', '', (v) => createMut.mutate({ name: v, parentId: null }))}
            >＋ 新建文件夹</button>
          )}
        </div>
        <div
          className="panel-body tree-body"
          onContextMenu={scopeKind === 'all'
            ? (e) => openMenu(e, { kind: 'root' })
            : undefined}
        >
          {treeQ.isLoading && <div className="hint">加载中…</div>}
          {treeQ.isError && (
            <div className="hint">加载失败：{(treeQ.error as any)?.message}</div>
          )}

          {scopeKind === 'all' ? (
            <>
              {tree && total === 0 && (
                <div className="hint">暂无图纸，请上传 PDF/PNG</div>
              )}

              {tree?.folders.map((n) => (
                <TreeNode
                  key={n.id}
                  node={n}
                  depth={0}
                  open={open}
                  onToggle={toggle}
                  onContext={openMenu}
                  onSelect={onSelect}
                  onOpenSip={onOpenSip}
                  selectedDrawingId={selectedDrawingId}
                  drawings={allDrawings}
                  onOpenPartsFolder={onOpenPartsFolder}
                />
              ))}

              {rootDrawings.length > 0 && (
                <>
                  {(tree?.folders.length ?? 0) > 0 && (
                    <div className="tree-rootlabel">
                      未归类（根目录） <em>({rootDrawings.length})</em>
                    </div>
                  )}
                  <DrawingItems
                    drawings={rootDrawings}
                    depth={0}
                    onContext={openMenu}
                    onSelect={onSelect}
                    onOpenSip={onOpenSip}
                    selectedDrawingId={selectedDrawingId}
                  />
                </>
              )}

              <div
                className="tree-blank"
                title="在此处右键 → 新建文件夹"
                onContextMenu={(e) => openMenu(e, { kind: 'root' })}
              />
            </>
          ) : (
            <>
              {/* 范围模式 = 平铺。文件夹是「上传批次」的分类，跟零件没关系，一律不渲染。 */}
              {!treeQ.isLoading && scoped.length === 0 && (
                <div className="hint">
                  {scopeKind === 'part'
                    ? '图纸列表是空的 —— 还没有上传过任何图纸。'
                    : '没有未归户的图纸 —— 每一张都已归户到某个零件。'}
                </div>
              )}
              {scopeKind === 'part' ? (
                <>
                  {!treeQ.isLoading && mine.length === 0 && rest.length > 0 && (
                    <div className="hint">
                      本零件名下还没有图纸 —— 下面列的是全部图纸；
                      可在图纸标题栏的零件栏把对应那张归户到本零件。
                    </div>
                  )}
                  {mine.length > 0 && (
                    <div className="tree-rootlabel">
                      本零件 <em>({mine.length})</em>
                    </div>
                  )}
                  <DrawingItems
                    drawings={mine}
                    depth={0}
                    onContext={openMenu}
                    onSelect={onSelect}
                    onOpenSip={onOpenSip}
                    selectedDrawingId={selectedDrawingId}
                    markPartId={minePartId}
                  />
                  {rest.length > 0 && (
                    <div className="tree-rootlabel">
                      其他图纸 <em>({rest.length})</em>
                    </div>
                  )}
                  <DrawingItems
                    drawings={rest}
                    depth={0}
                    onContext={openMenu}
                    onSelect={onSelect}
                    onOpenSip={onOpenSip}
                    selectedDrawingId={selectedDrawingId}
                  />
                </>
              ) : (
                <DrawingItems
                  drawings={scoped}
                  depth={0}
                  onContext={openMenu}
                  onSelect={onSelect}
                  onOpenSip={onOpenSip}
                  selectedDrawingId={selectedDrawingId}
                />
              )}
            </>
          )}
        </div>
      </div>

      {ctx && (
        <ContextMenu
          ctx={ctx}
          menuRef={menuRef}
          // 范围模式（零件 / 未归户）里没有文件夹概念，「移动到…」没有落点 ⇒ 不提供。
          allowMove={scopeKind === 'all'}
          onClose={() => setCtx(null)}
          onNewFolder={() => askName(
            ctx.kind === 'folder' ? '新建子文件夹' : '新建文件夹', '',
            (v) => createMut.mutate({
              name: v, parentId: ctx.kind === 'folder' ? (ctx.folderId ?? null) : null,
            }))}
          onRename={() => {
            const f = foldersFlat.find((x) => x.id === ctx.folderId);
            if (f) askName('重命名文件夹', f.name,
              (v) => renameMut.mutate({ id: f.id, name: v }));
          }}
          onDeleteFolder={() => {
            const f = foldersFlat.find((x) => x.id === ctx.folderId);
            if (!f) return;
            const inner = f.drawing_count;
            const all = f.total_drawing_count;
            const msg = ctx.kind !== 'folder' ? '' :
              (all > inner
                ? `文件夹「${f.name}」及其 ${countFolders(f)} 个子文件夹都会被删除。`
                : `文件夹「${f.name}」会被删除。`) +
              (all ? `其中共 ${all} 张图纸会**退回根目录**（图纸不会被删）。` : '') +
              // v0.57：这棵树是**图纸与零件共用**的，所以删夹子对**两个页签**
              // 都有影响。只说图纸的话，用户切到零件主数据会以为零件也丢了。
              (f.total_part_count
                ? `其中 ${f.total_part_count} 个零件会**退回未分类**（零件不会被删）。`
                : '');
            if (confirm(`确定删除文件夹「${f.name}」？\n\n${msg}`)) {
              delFolderMut.mutate(f.id);
            }
          }}
          onMoveFolder={() => {
            const f = foldersFlat.find((x) => x.id === ctx.folderId);
            if (!f) return;
            askMove(`移动文件夹「${f.name}」到…`, f.parent_id,
              (v) => moveFolderMut.mutate({ id: f.id, parentId: v }));
          }}
          onMoveDrawing={() => {
            const d = ctx.drawing;
            if (!d) return;
            askMove(`移动图纸「${d.filename}」到…`, null,
              (v) => moveDrawingMut.mutate({ id: d.id, folderId: v }));
          }}
          onDeleteDrawing={() => {
            const d = ctx.drawing;
            if (!d) return;
            // v0.56：删除已进**回收站**（`trash_entries`，图纸连同其特征一起快照），
            // 所以这里**不能**再写「不可撤销」—— 那句已经不成立，会把人吓退。
            // 也如实说清「SIP 草稿会一并清掉」（后端 `repo.delete_drawing` 显式删
            // `sip_drafts`：省掉它的话，图纸 id 被复用后新图会长出旧图的草稿）。
            if (confirm(`删除图纸 ${d.filename}?\n\n`
                        + `这会删除该图纸与其全部特征、以及它的作业指导书草稿。\n`
                        + `删除后可在「回收站」里恢复。`)) {
              delDrawingMut.mutate(d.id);
            }
          }}
        />
      )}

      {dialog && (
        <Dialog
          state={dialog}
          onCancel={() => setDialog(null)}
          // 必须先按 kind 收窄再调 onOk：两种对话框的 onOk 参数类型不同，
          // 直接 `dialog.onOk(v as any)` 会被 TS 当成「联合函数」，
          // 形参取交叉类型 → 报 `Argument of type 'any' is not assignable to
          // parameter of type 'never'`（看着像 any 的问题，其实是联合收窄）。
          onSubmit={(v) => {
            if (dialog.kind === 'input') dialog.onOk(String(v ?? ''));
            else dialog.onOk(v as number | null);
            setDialog(null);
          }}
        />
      )}
    </aside>
  );
}

// ================================================================ 子树渲染
function TreeNode({
  node, depth, open, onToggle, onContext, onSelect, onOpenSip, selectedDrawingId,
  drawings, onOpenPartsFolder,
}: {
  node: FolderNode;
  depth: number;
  open: Set<number>;
  onToggle: (id: number) => void;
  onContext: (e: React.MouseEvent, c: Omit<Ctx, 'x' | 'y'>) => void;
  /** `id=null` = 清空选中（图纸被删时用）。 */
  onSelect: (id: number | null, d?: DrawingSummary) => void;
  selectedDrawingId: number | null;
  drawings: DrawingSummary[];
  onOpenSip?: (e: SipEntry) => void;
  /** v0.56：点「M 个零件」跳到零件主数据的该文件夹（见 Props 上的说明）。 */
  onOpenPartsFolder?: (folderId: number) => void;
}) {
  const isOpen = open.has(node.id);
  // v0.56：能不能展开**要把零件算进去**。只看图纸数的话，一个"只装了零件、
  // 没有图纸"的夹子三角是灰的（`·`）—— 用户在零件主数据里明明归类成功了，
  // 回到图纸区却像"这个夹子不存在内容"，正是他报的「没统一变动」。
  const hasKids = node.children.length > 0 || node.drawing_count > 0
    || node.part_count > 0;
  const inner = useMemo(
    () => drawings.filter((d) => (d as any).folder_id === node.id), [drawings, node.id]);
  /** 折叠态下也要能看出「这个夹子里有多少」——两个数都带「含子孙」的上限。 */
  const drawTxt = node.total_drawing_count > node.drawing_count
    ? `${node.drawing_count}/${node.total_drawing_count}`
    : `${node.drawing_count}`;
  const partTxt = node.total_part_count > node.part_count
    ? `${node.part_count}/${node.total_part_count}`
    : `${node.part_count}`;
  return (
    <>
      <div
        className={`tree-folder${isOpen ? ' open' : ''}`}
        style={{ paddingLeft: 4 + depth * 14 }}
        onContextMenu={(e) => onContext(e, { kind: 'folder', folderId: node.id })}
        onClick={() => hasKids && onToggle(node.id)}
        title={`${node.name}\n`
               + `图纸：本层 ${node.drawing_count} 张，含子文件夹 ${node.total_drawing_count} 张\n`
               + `零件：本层 ${node.part_count} 个，含子文件夹 ${node.total_part_count} 个`
               + `（点零件数可跳去零件主数据）\n`
               + `右键可新建子文件夹/重命名/删除/移动`}
      >
        <span className="tw">{hasKids ? (isOpen ? '▾' : '▸') : '·'}</span>
        <span className="fi">{isOpen ? '📂' : '📁'}</span>
        <span className="fname">{node.name}</span>
        {/* v0.56 双计数（用户：「三者需要联动」）：
            左 = 图纸、右 = 零件，**同一棵树同一份计数**，单位写全（「张图纸」
            「个零件」）—— 只写 `2` `3` 两个裸数字，没人分得清哪个是哪个。
            ⚠️ 零件那一格是**独立可点**的（`stopPropagation` 掐掉外层的折叠），
               点它跳到零件主数据的同一个文件夹。 */}
        <span className="fcount" title={`图纸：本层 ${node.drawing_count} 张，含子文件夹 ${node.total_drawing_count} 张`}>
          {drawTxt}<em className="fcount-u">张图纸</em>
        </span>
        {onOpenPartsFolder && (
          <span className="fcount fcount-parts"
                role="button" tabIndex={0}
                data-parts-folder={node.id}
                title={`这个文件夹里有 ${node.part_count} 个零件（含子文件夹 ${node.total_part_count} 个）—— 点击去零件主数据查看`}
                onClick={(e) => {
                  // [!] 必须掐冒泡：外层那一下是「折叠/展开」，
                  //     不掐的话点零件数会顺手把夹子收起来，看着像"点错了"。
                  e.stopPropagation();
                  onOpenPartsFolder(node.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault(); e.stopPropagation();
                    onOpenPartsFolder(node.id);
                  }
                }}>
            {partTxt}<em className="fcount-u">个零件</em>
          </span>
        )}
      </div>
      {isOpen && (
        <>
          {node.children.map((c) => (
            <TreeNode
              key={c.id}
              node={c}
              depth={depth + 1}
              open={open}
              onToggle={onToggle}
              onContext={onContext}
              onSelect={onSelect}
              onOpenSip={onOpenSip}
              selectedDrawingId={selectedDrawingId}
              drawings={drawings}
              onOpenPartsFolder={onOpenPartsFolder}
            />
          ))}
          <DrawingItems
            drawings={inner}
            depth={depth + 1}
            onContext={onContext}
            onSelect={onSelect}
            onOpenSip={onOpenSip}
            selectedDrawingId={selectedDrawingId}
          />
          {/* v0.56：展开之后**必须给点东西看**。一个只装了零件的夹子，展开后
              子夹与图纸都为空 ⇒ 一片空白，用户会当成「点坏了」。
              这里如实说明，并再给一次跳转入口（折叠态那个小徽标太容易漏看）。 */}
          {inner.length === 0 && node.children.length === 0 && node.part_count > 0 && (
            <div className="tree-partnote"
                 style={{ paddingLeft: 18 + (depth + 1) * 14 }}
                 title="图纸与零件的文件夹归属各归各的 —— 这里只做跳转，不会搬运数据"
                 onClick={(e) => {
                   e.stopPropagation();
                   onOpenPartsFolder?.(node.id);
                 }}>
              📦 这个文件夹里没有图纸，但有 {node.part_count} 个零件
              {onOpenPartsFolder ? ' —— 点击去零件主数据查看' : ''}
            </div>
          )}
        </>
      )}
    </>
  );
}

function DrawingItems({
  drawings, depth, onContext, onSelect, selectedDrawingId, markPartId, onOpenSip,
}: {
  drawings: DrawingSummary[];
  depth: number;
  onContext: (e: React.MouseEvent, c: Omit<Ctx, 'x' | 'y'>) => void;
  /** `id=null` = 清空选中（图纸被删时用）。 */
  onSelect: (id: number | null, d?: DrawingSummary) => void;
  selectedDrawingId: number | null;
  onOpenSip?: (e: SipEntry) => void;
  /** v0.42：`part` 范围下要**标出来**的零件 id（属于它的那张打「本零件」徽标 + 高亮）。
   *  只在置顶那一段传；下面「其他图纸」那一段不传 ⇒ 同一张图不会同时戴两种标记。 */
  markPartId?: number | null;
}) {
  return (
    <>
      {drawings.map((d) => {
        const isMine = markPartId != null && d.part_master_id === markPartId;
        return (
          <div
            key={d.id}
            className={`tree-file${selectedDrawingId === d.id ? ' active' : ''}${isMine ? ' mine' : ''}`}
            style={{ paddingLeft: 18 + depth * 14 }}
            onClick={(e) => { e.stopPropagation(); onSelect(d.id, d); }}
            onContextMenu={(e) => onContext(e, { kind: 'drawing', drawing: d })}
            title={`#${d.id} ${d.filename}\n${d.feature_count} 特征 · ${d.page_count} 页\n右键：移动到… / 删除图纸\n左下角「SIP」：编辑这张图纸的作业指导书`}
          >
            <span className="fi">📄</span>
            <span className="fname">{d.filename}</span>
            {isMine && <em className="tree-mine" title="这张图纸已归户到当前零件">本零件</em>}
            {onOpenSip && (
              <button className="tree-sip"
                      title="编辑这张图纸的作业指导书 SIP（原材料 / 制程 / 成品 / 出货 / 自检）"
                      data-sip-drawing={d.id}
                      onClick={(e) => {
                        // [!] 必须掐掉冒泡：这一行外层的 onClick 是「选中图纸」，
                        //     不掐的话点 SIP 会**顺手把图纸也选中**（右侧换图），
                        //     用户看到的是「我没点那儿，怎么图变了」。
                        e.stopPropagation();
                        onOpenSip({ kind: 'drawing', id: d.id,
                                    label: `#${d.id} ${d.filename}` });
                      }}>SIP</button>
            )}
            <span className="fcount">{d.feature_count}</span>
          </div>
        );
      })}
    </>
  );
}

// ================================================================ 右键菜单
function ContextMenu({
  ctx, menuRef, allowMove, onClose, onNewFolder, onRename, onDeleteFolder, onMoveFolder,
  onMoveDrawing, onDeleteDrawing,
}: {
  ctx: Ctx;
  /** 菜单根 DOM：父组件的 capture-mousedown 靠 `contains(e.target)` 判断「点在菜单内」。 */
  menuRef: React.RefObject<HTMLDivElement>;
  /** v0.40：范围模式（零件 / 未归户）下为 false —— 那里没有文件夹，「移动到…」无落点。 */
  allowMove: boolean;
  onClose: () => void;
  onNewFolder: () => void;
  onRename: () => void;
  onDeleteFolder: () => void;
  onMoveFolder: () => void;
  onMoveDrawing: () => void;
  onDeleteDrawing: () => void;
}) {
  const Item = ({ label, onClick, danger }: any) => (
    <button
      className={`ctx-item${danger ? ' danger' : ''}`}
      onClick={(e) => { e.stopPropagation(); onClose(); onClick(); }}
    >{label}</button>
  );
  // ⚠️ 下面 `<div>` 上的 `onMouseDown={e => e.stopPropagation()}` **不是**
  //    「点菜单不关」的机制 —— capture 阶段的 window 监听跑在它之前，拦不住。
  //    真正的开关在父组件的 `menuRef.contains(e.target)` 判定。留着只为挡住其它
  //    冒泡处理器，别据此把父组件那半删掉。
  return (
    <div className="ctx-menu" ref={menuRef} style={{ left: ctx.x, top: ctx.y }}
         onMouseDown={(e) => e.stopPropagation()}
         onContextMenu={(e) => e.preventDefault()}>
      {ctx.kind === 'root' && <Item label="新建文件夹" onClick={onNewFolder} />}
      {ctx.kind === 'folder' && (
        <>
          <Item label="新建子文件夹" onClick={onNewFolder} />
          <Item label="重命名" onClick={onRename} />
          <Item label="移动到…" onClick={onMoveFolder} />
          <div className="ctx-sep" />
          <Item label="删除文件夹（图纸退回根目录）" onClick={onDeleteFolder} danger />
        </>
      )}
      {ctx.kind === 'drawing' && (
        <>
          {allowMove && <Item label="移动到…" onClick={onMoveDrawing} />}
          {allowMove && <div className="ctx-sep" />}
          <Item label="删除图纸" onClick={onDeleteDrawing} danger />
        </>
      )}
    </div>
  );
}

// ================================================================ 输入 / 选择对话框
type DialogState =
  | { kind: 'input'; title: string; initial: string; hint: string; onOk: (v: string) => void }
  | { kind: 'move'; title: string; current: number | null;
      folders: Array<{ id: number; label: string }>; onOk: (v: number | null) => void };

function Dialog({
  state, onCancel, onSubmit,
}: {
  state: DialogState;
  onCancel: () => void;
  onSubmit: (v: string | number | null) => void;
}) {
  const [text, setText] = useState(state.kind === 'input' ? state.initial : '');
  const [sel, setSel] = useState<number | null>(
    state.kind === 'move' ? state.current : null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (state.kind === 'input') {
      setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 30);
    }
  }, [state]);

  const ok = () => {
    if (state.kind === 'input') {
      const v = text.trim();
      if (!v) return;
      onSubmit(v);
    } else {
      onSubmit(sel);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onCancel} />
      <div className="modal tree-dialog" role="dialog" aria-modal="true"
           onKeyDown={(e) => {
             if (e.key === 'Enter') ok();
             if (e.key === 'Escape') onCancel();
           }}>
        <div className="modal-head">
          <div className="modal-title">{state.title}</div>
          <button className="btn sm" onClick={onCancel} title="关闭">✕</button>
        </div>
        <div className="modal-body">
          {state.kind === 'input' ? (
            <>
              <input
                ref={inputRef}
                className="modal-input"
                value={text}
                placeholder="文件夹名称"
                onChange={(e) => setText(e.target.value)}
              />
              <div className="modal-hint">{state.hint}</div>
            </>
          ) : (
            <>
              <select
                className="modal-input"
                value={sel == null ? '' : String(sel)}
                onChange={(e) => setSel(e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">— 根目录（不放进任何文件夹）—</option>
                {state.folders.map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
              <div className="modal-hint">
                不能移动到它自己或它的子文件夹里（会成环，后端会拒绝）。
              </div>
            </>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onCancel}>取消</button>
          <button className="btn primary" onClick={ok}
                  disabled={state.kind === 'input' && !text.trim()}>确定</button>
        </div>
      </div>
    </>
  );
}

// ================================================================ 工具
interface FlatFolder extends FolderNode { path: string }

function flatten(nodes: FolderNode[], prefix = ''): FlatFolder[] {
  const out: FlatFolder[] = [];
  for (const n of nodes) {
    const path = prefix ? `${prefix} / ${n.name}` : n.name;
    out.push({ ...n, path });
    if (n.children?.length) out.push(...flatten(n.children, path));
  }
  return out;
}

function countFolders(n: FolderNode): number {
  let t = 0;
  for (const c of n.children ?? []) { t += 1 + countFolders(c); }
  return t;
}
